import { useRef, useState } from 'react';

import { isEmpty } from 'dpl/shared/utils/object';
import customValidate from 'dpl/shared/validations/customValidate';
import createFormSchema from 'dpl/shared/validations/createFormSchema';

export default function useValidation(mapFormStateToValidationSchema) {
  const [errors, setErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);

  /**
   * Addresses race-condition issues where validation relies on network and a
   * stale validation promise resolves after the most recent one. Kept in a ref
   * so the check survives re-renders: every keystroke re-renders the form, and
   * a per-render variable let each stale promise believe it was the latest.
   */
  const lastPromiseRef = useRef(null);

  function validate(formState) {
    const schema = mapFormStateToValidationSchema(formState);

    if (!schema) {
      return Promise.resolve();
    }

    const formSchema = createFormSchema(schema);

    setIsValidating(true);

    const _thisPromise = customValidate
      .async(formState, formSchema, {
        fullMessages: false,
        // This is needed so that validate.js doesn't drop array constraints
        // whose keys are re-constructed subsequently w/in `runValidations` (see in
        // customValidate.js)
        cleanAttributes: false
      })
      .then(() => {
        if (_thisPromise === lastPromiseRef.current) {
          setIsValidating(false);
          setErrors({});
        }
      })
      .catch(_errors => {
        if (_thisPromise === lastPromiseRef.current) {
          setIsValidating(false);
          setErrors(_errors);
        }
      });

    lastPromiseRef.current = _thisPromise;

    return _thisPromise;
  }

  return {
    errors,
    isValidating,
    validate,
    isValid: isEmpty(errors)
  };
}
